import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Mail, Phone, Lock, Loader2, ArrowRight, Eye, EyeOff } from "lucide-react";
import logo from "@/assets/logo.svg";

export default function LoginPage() {
  const { user, login, loading } = useAuth();
  const navigate = useNavigate();

  const [authType, setAuthType] = useState<"email" | "phone">("email");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // If already logged in, redirect to overview
  if (user && user.role === "admin" && !loading) {
    return <Navigate to="/" replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validations
    if (authType === "email" && !email) {
      toast.error("Please enter your email address");
      return;
    }
    if (authType === "phone" && !phone) {
      toast.error("Please enter your phone number");
      return;
    }
    if (!password || password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setSubmitting(true);
    const toastId = toast.loading("Authenticating administrative session...");

    try {
      const payload = authType === "email" ? { email, password } : { phone, password };

      await login(payload);
      toast.success("Welcome back, Administrator!", { id: toastId });
      navigate("/");
    } catch (err: any) {
      toast.error(err.message || "Failed to log in. Please check your credentials.", {
        id: toastId,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-brand-canvas p-4 text-foreground relative overflow-hidden">
      {/* Ornate Background Accents */}
      <div className="absolute top-[-10%] left-[-10%] h-[40%] w-[40%] rounded-full bg-gold/5 blur-[120px]" />
      <div className="absolute bottom-[-10%] right-[-10%] h-[40%] w-[40%] rounded-full bg-gold/5 blur-[120px]" />

      <div className="w-full max-w-md">
        {/* Brand Header */}
        <div className="mb-8 flex flex-col items-center text-center">
          <div className="rounded-full border border-gold p-1.5 shadow-[0_0_25px_rgba(212,175,55,0.15)] bg-sidebar">
            <img src={logo} alt="Subaashghor" className="h-20 w-20 rounded-full object-contain" />
          </div>
          <h1 className="mt-4 font-display text-3xl tracking-wide text-gold">সুবাসঘর</h1>
          <p className="text-[10px] tracking-[0.4em] uppercase text-gold-soft">Subaashghor</p>
          <div className="ornate-divider mt-3 w-32" />
          <p className="mt-2 text-xs text-muted-foreground/80 tracking-wide font-medium">Authorized Administrative Access</p>
        </div>

        {/* Card */}
        <div className="ornate-corners gold-shadow rounded-2xl border border-gold/20 bg-sidebar/95 p-8 backdrop-blur-md">
          {/* Tabs */}
          <div className="mb-6 flex rounded-lg bg-card/65 p-1 border border-gold/10">
            <button
              type="button"
              onClick={() => setAuthType("email")}
              className={`flex flex-1 items-center justify-center gap-2 rounded-md py-2.5 text-xs font-medium tracking-wide transition-all ${
                authType === "email"
                  ? "bg-accent text-gold shadow-sm border border-gold/25"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <Mail className="h-3.5 w-3.5" />
              Email Address
            </button>
            <button
              type="button"
              onClick={() => setAuthType("phone")}
              className={`flex flex-1 items-center justify-center gap-2 rounded-md py-2.5 text-xs font-medium tracking-wide transition-all ${
                authType === "phone"
                  ? "bg-accent text-gold shadow-sm border border-gold/25"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <Phone className="h-3.5 w-3.5" />
              Phone Number
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {authType === "email" ? (
              <div className="space-y-1.5">
                <label className="text-xs font-medium tracking-wider text-gold-soft uppercase">
                  Email Address
                </label>
                <div className="relative flex items-center">
                  <Mail className="absolute left-3 h-4 w-4 text-gold/60" />
                  <input
                    type="email"
                    placeholder="admin@subaashghor.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full rounded-lg border border-gold/20 bg-card/50 py-3 pl-10 pr-4 text-sm text-foreground outline-none transition-all placeholder:text-muted-foreground/50 focus:border-gold/50 focus:bg-card focus:shadow-[0_0_10px_rgba(212,175,55,0.05)]"
                    disabled={submitting}
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-1.5">
                <label className="text-xs font-medium tracking-wider text-gold-soft uppercase">
                  Phone Number
                </label>
                <div className="relative flex items-center">
                  <Phone className="absolute left-3 h-4 w-4 text-gold/60" />
                  <input
                    type="text"
                    placeholder="01712345678"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="w-full rounded-lg border border-gold/20 bg-card/50 py-3 pl-10 pr-4 text-sm text-foreground outline-none transition-all placeholder:text-muted-foreground/50 focus:border-gold/50 focus:bg-card focus:shadow-[0_0_10px_rgba(212,175,55,0.05)]"
                    disabled={submitting}
                  />
                </div>
              </div>
            )}

            <div className="space-y-1.5">
              <div className="flex justify-between items-center">
                <label className="text-xs font-medium tracking-wider text-gold-soft uppercase">
                  Security Password
                </label>
              </div>
              <div className="relative flex items-center">
                <Lock className="absolute left-3 h-4 w-4 text-gold/60" />
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full rounded-lg border border-gold/20 bg-card/50 py-3 pl-10 pr-10 text-sm text-foreground outline-none transition-all placeholder:text-muted-foreground/50 focus:border-gold/50 focus:bg-card focus:shadow-[0_0_10px_rgba(212,175,55,0.05)]"
                  disabled={submitting}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 text-gold/60 hover:text-gold transition-colors focus:outline-none"
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <EyeOff className="h-4.5 w-4.5" />
                  ) : (
                    <Eye className="h-4.5 w-4.5" />
                  )}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="group relative flex w-full items-center justify-center gap-2 rounded-lg border border-gold/30 bg-accent py-3.5 text-sm font-semibold tracking-wider text-gold shadow-md transition-all hover:bg-gold hover:text-sidebar active:scale-[0.98] disabled:opacity-50 disabled:pointer-events-none"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Authenticating...
                </>
              ) : (
                <>
                  Enter Control Panel
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </>
              )}
            </button>
          </form>
        </div>

        {/* Brand Footer */}
        <p className="mt-8 text-center text-[10px] text-muted-foreground tracking-wider">
          সুবাসঘর — A House of Pure Fragrance · Secure Admin Gateway
        </p>
      </div>
    </div>
  );
}
